/**
 * GLB URL resolver — turns a cross-origin model URL into something the
 * Three.js loader can actually fetch.
 *
 * Resolution order:
 *   1. success cache (blob URLs / proxied URLs from earlier resolutions)
 *   2. LOCAL DEV: the dev server's same-origin proxies (src/setupProxy.js) —
 *      /evfiles/... for the blob host and /ev/upload/... for the legacy host.
 *      Same-origin means CORS never enters the picture (Safari included).
 *   3. direct fetch -> blob URL (works wherever the file host sends CORS
 *      headers; Chrome is lenient, Safari is not)
 *
 * On total failure it THROWS. It must never hand back the original URL "as a
 * fallback": that URL already failed, and feeding it to useGLTF just moves
 * the same failure into the canvas where it churns WebGL contexts.
 *
 * Two guards make render-loop mistakes harmless at the network layer:
 *   * single-flight — concurrent calls for one URL share one resolution;
 *   * failure memo — a URL that just failed rejects instantly for 60s
 *     (clearGLBFailure(url) is the manual-retry escape hatch).
 * Before these, Safari's failing loads re-resolved on every render and fired
 * an endless stream of requests (observed 2026-08-07).
 */
import Config from '../Config.jsx';

// url -> resolved URL (blob: or same-origin proxy). LRU with blob revocation.
const glbCache = new Map();
const GLB_CACHE_MAX = 8;

// url -> in-flight resolution promise (single-flight).
const inflight = new Map();

// url -> timestamp of the last total failure (negative cache).
const failedAt = new Map();
const FAILURE_MEMO_MS = 60 * 1000;

function cacheGLBUrl(url, resolvedUrl) {
  if (glbCache.has(url)) {
    glbCache.delete(url); // refresh recency
  }
  glbCache.set(url, resolvedUrl);

  while (glbCache.size > GLB_CACHE_MAX) {
    const oldestUrl = glbCache.keys().next().value;
    const oldest = glbCache.get(oldestUrl);
    if (typeof oldest === 'string' && oldest.startsWith('blob:')) {
      URL.revokeObjectURL(oldest);
    }
    glbCache.delete(oldestUrl);
  }
}

function isLocalDev() {
  return typeof window !== 'undefined' && window.location &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
}

function uploadHostname() {
  try {
    return new URL(Config.UPLOAD_PATH()).hostname;
  } catch (e) {
    return 'files.ev-cuhk.net';
  }
}

/** Same-origin dev-proxy candidates for this URL (empty outside local dev). */
function sameOriginCandidates(originalUrl) {
  if (!isLocalDev()) {
    return [];
  }
  try {
    const u = new URL(originalUrl);
    const candidates = [];
    if (u.hostname === uploadHostname() || u.hostname === 'files.ev-cuhk.net') {
      candidates.push(`${window.location.origin}/evfiles${u.pathname}`);
    }
    if (u.hostname === 'ev-cuhk.net' && u.pathname.startsWith('/upload/')) {
      candidates.push(`${window.location.origin}/ev${u.pathname}`);
    }
    return candidates;
  } catch (e) {
    return [];
  }
}

async function fetchAsBlobUrl(url) {
  const response = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error('empty response');
  }
  return URL.createObjectURL(blob);
}

async function resolveOnce(originalUrl) {
  // Same-origin proxy first (dev): a HEAD confirms the proxy is alive and not
  // answering with the dev server's index.html.
  for (const candidate of sameOriginCandidates(originalUrl)) {
    try {
      const head = await fetch(candidate, { method: 'HEAD' });
      const contentType = (head.headers.get('content-type') || '').toLowerCase();
      if (head.ok && !contentType.includes('text/html')) {
        return candidate;
      }
    } catch (e) {
      // proxy route absent/down — try the next option
    }
  }

  // Direct fetch, kept as a blob so the loader never re-downloads.
  return fetchAsBlobUrl(originalUrl);
}

/**
 * Resolve a model URL for the Three.js loader. Resolves to a blob: URL or a
 * same-origin proxy URL; REJECTS when the file is unreachable from this
 * browser (the caller shows an error state with a manual retry).
 */
export function getGLBUrl(originalUrl) {
  if (glbCache.has(originalUrl)) {
    const cached = glbCache.get(originalUrl);
    cacheGLBUrl(originalUrl, cached); // refresh recency
    return Promise.resolve(cached);
  }

  const lastFailure = failedAt.get(originalUrl);
  if (lastFailure && Date.now() - lastFailure < FAILURE_MEMO_MS) {
    return Promise.reject(new Error('The model file is not reachable from this browser right now.'));
  }

  if (inflight.has(originalUrl)) {
    return inflight.get(originalUrl);
  }

  const resolution = resolveOnce(originalUrl)
    .then((resolved) => {
      failedAt.delete(originalUrl);
      cacheGLBUrl(originalUrl, resolved);
      return resolved;
    })
    .catch((error) => {
      failedAt.set(originalUrl, Date.now());
      console.warn('GLB unreachable from this browser:', originalUrl, error && error.message);
      throw new Error('The model file could not be loaded (blocked by the browser’s cross-origin rules).');
    })
    .finally(() => {
      inflight.delete(originalUrl);
    });

  inflight.set(originalUrl, resolution);
  return resolution;
}

/** Forget a recorded failure so a user-initiated retry resolves afresh. */
export function clearGLBFailure(url) {
  failedAt.delete(url);
}

/** Download a GLB into a cached blob URL (legacy helper, same guards). */
export async function downloadGLBAsBlob(url) {
  return getGLBUrl(url);
}

/** Warm the cache; resolves false instead of rejecting. */
export async function preloadGLB(url) {
  try {
    await getGLBUrl(url);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Revoke a single blob URL (and drop it from the cache) when its model is
 * disposed or replaced. Accepts either the original source URL (cache key) or
 * the blob URL itself.
 */
export function revokeGLBUrl(url) {
  if (!url) {
    return;
  }

  if (glbCache.has(url)) {
    const resolved = glbCache.get(url);
    if (typeof resolved === 'string' && resolved.startsWith('blob:')) {
      URL.revokeObjectURL(resolved);
    }
    glbCache.delete(url);
    return;
  }

  if (typeof url === 'string' && url.startsWith('blob:')) {
    glbCache.forEach((resolved, key) => {
      if (resolved === url) {
        glbCache.delete(key);
      }
    });
    URL.revokeObjectURL(url);
  }
}

/** Clear the GLB cache to free memory. */
export function clearGLBCache() {
  glbCache.forEach((resolved) => {
    if (typeof resolved === 'string' && resolved.startsWith('blob:')) {
      URL.revokeObjectURL(resolved);
    }
  });
  glbCache.clear();
}

export function isGLBCached(url) {
  return glbCache.has(url);
}

export function getGLBCacheStats() {
  return {
    size: glbCache.size,
    urls: Array.from(glbCache.keys())
  };
}

// Clean up blob URLs when the page unloads
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', clearGLBCache);
}
